import { useLayoutEffect } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@stores/authStore';
import { useThemeStore } from '@stores/themeStore';
import { applyTheme, getTheme } from '@/theme';
import { ThemeToggle } from '@components/ThemeToggle';
import { AuthSplitScreen } from './AuthSplitScreen';

/**
 * Routes that get the split sign-in screen. Everything else sharing this
 * layout — forgot-password, reset-password, account setup, the SSO callback —
 * keeps the plain centred presentation below, unchanged.
 */
const SPLIT_ROUTES = new Set(['/login', '/platform-admin', '/platform-admin/register']);

/** The sign-in screens always wear Graphite & Azure, whatever theme the app uses. */
const AUTH_THEME_ID = getTheme('graphite-azure').id;

/**
 * Paint the auth screens in Graphite while they are mounted (re-applied when
 * light/dark flips, since the store repaints with the saved theme), and hand
 * the user's own theme back on the way into the app.
 */
function useAuthTheme() {
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme);
  useLayoutEffect(() => {
    applyTheme(AUTH_THEME_ID, resolvedTheme);
  }, [resolvedTheme]);
  useLayoutEffect(
    () => () => {
      const { appTheme, resolvedTheme: mode } = useThemeStore.getState();
      applyTheme(appTheme, mode);
    },
    [],
  );
}

export default function AuthLayout() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { pathname } = useLocation();
  useAuthTheme();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  if (SPLIT_ROUTES.has(pathname)) {
    return (
      <AuthSplitScreen>
        <Outlet />
      </AuthSplitScreen>
    );
  }

  return (
    // Anchored to the top rather than centred: centring moved the logo to a
    // different height on every page, because each page's content is a
    // different height.
    <div className="min-h-screen flex items-start justify-center bg-[var(--co-canvas)] px-4 pb-12 pt-[max(48px,14vh)] sm:px-6 lg:px-8">
      {/* Theme toggle in corner */}
      <div className="fixed top-4 right-4">
        <ThemeToggle />
      </div>

      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img
            src="/assets/logos/logo-color.png"
            alt="CentraOne"
            className="h-10 mx-auto dark:hidden"
          />
          <img
            src="/assets/logos/logo-white.png"
            alt="CentraOne"
            className="h-10 mx-auto hidden dark:block"
          />
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Service Desk Platform</p>
        </div>
        <Outlet />
      </div>
    </div>
  );
}
